import { useState } from 'react';

import { Trans } from '@lingui/react/macro';
import { cn } from '@repo/videa-components/src/lib/utils';
import { Button } from '@repo/videa-components/src/ui/button';
import { Calendar } from '@repo/videa-components/src/ui/calendar';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@repo/videa-components/src/ui/popover';
import { format } from 'date-fns';
import { CalendarIcon } from 'lucide-react';

import { useGetDateFNSLocale } from '../../../locales/helpers';

type DateRange = {
  from?: Date;
  to?: Date;
};

export function DatePickerWithRange({
  className,
  date,
  setDate,
}: {
  className?: string;
  date?: DateRange;
  setDate: (date: DateRange | undefined) => void;
}) {
  // state
  const [isOpen, setIsOpen] = useState(false);

  // hooks
  const dateLocale = useGetDateFNSLocale();

  // handlers
  const handleDateSelect = (selectedDate: Date | undefined) => {
    if (!selectedDate) {
      setDate(undefined);
      return;
    }

    // If no range is selected, start a new range with this date as "from"
    if (!date?.from) {
      setDate({ from: selectedDate, to: undefined });
      return;
    }

    // If we have a "from" date but no "to" date
    if (date.from && !date.to) {
      // If the selected date is before the "from" date, make it the new "from" date
      if (selectedDate < date.from) {
        setDate({ from: selectedDate, to: date.from });
        return;
      }
      // If the selected date is after the "from" date, make it the "to" date
      if (selectedDate > date.from) {
        setDate({ from: date.from, to: selectedDate });
        return;
      }
      // If it's the same date, clear the selection
      setDate(undefined);
      return;
    }

    // If we have both "from" and "to" dates, clicking any date starts a new range
    // with that date as the new "from" date
    setDate({ from: selectedDate, to: undefined });
  };

  return (
    <div className={cn('vai-grid vai-gap-2', className)}>
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            id="date"
            variant="outline"
            className={cn(
              'vai-w-[250px] vai-justify-start vai-text-left vai-font-normal',
              !date && 'vai-text-muted-foreground',
            )}
          >
            <CalendarIcon />
            {date?.from ? (
              date.to ? (
                <>
                  {format(date.from, 'LLL dd, y', { locale: dateLocale })} -{' '}
                  {format(date.to, 'LLL dd, y', { locale: dateLocale })}
                </>
              ) : (
                format(date.from, 'LLL dd, y', { locale: dateLocale })
              )
            ) : (
              <span>
                <Trans>Pick a date</Trans>
              </span>
            )}
          </Button>
        </PopoverTrigger>
        <PopoverContent className="vai-w-auto vai-p-0" align="start">
          <Calendar
            initialFocus
            mode="single"
            defaultMonth={date?.from}
            selected={date?.from}
            onSelect={handleDateSelect}
            numberOfMonths={2}
            // Show the range visually even though we're using single mode
            modifiers={{
              ...(date?.from && { range_start: date.from }),
              ...(date?.to && { range_end: date.to }),
              ...(date?.from &&
                date?.to && {
                  range_middle: (d) => d > date.from! && d < date.to!,
                }),
            }}
            modifiersClassNames={{
              range_end:
                'vai-bg-primary vai-text-primary-foreground hover:vai-bg-primary hover:vai-text-primary-foreground',
              range_middle: 'vai-bg-gray-100 vai-text-gray-700',
            }}
            locale={dateLocale}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
